import { ApolloError, useApolloClient } from "@apollo/client";
import { buildInvoiceFileName } from "@msys/common";
import { MenuButton, MenuItemWithIcon, ModalOpenButton } from "@msys/ui";
import { GetApp as DownloadIcon } from "@mui/icons-material";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import { Settings as SettingsIcon } from "@mui/icons-material";
import {
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";
import { useNavigate } from "react-router-dom";
import type { EInvoiceFormat } from "../../../clients/graphqlTypes.js";
import { RestrictedByProjectPermissionWithDebug } from "../../auth/RestrictedByProjectPermission.js";
import { useUserData } from "../../auth/useUserData.js";
import { EntityNumber } from "../../commons/EntityNumber.js";
import { useOpenFile } from "../../commons/hooks/useOpenFile.js";
import {
  PageSubHeader,
  PageSubHeaderInjectedProps,
} from "../../commons/layout/PageSubHeader.js";
import { InvoiceDraftDeleteMenuItem } from "./InvoiceDraftDeleteMenuItem.js";
import {
  splitMissingFields,
  SERVICE_PERIOD_MISSING_KEY,
} from "./eInvoicePreconditionMessage.js";
import { useModifyInvoiceMutation } from "./Invoices.generated.js";
import { InvoiceSettingsModal } from "./InvoiceSettingsModal.js";
import { InvoiceStatusChip } from "./InvoiceStatusBadge.js";
import {
  InvoiceSubHeader_InvoiceFragment,
  InvoiceSubHeader_ProjectFragment,
  useInvoiceSubHeader_RegenerateInvoiceDocumentsMutation,
} from "./InvoiceSubHeader.generated.js";

const MISSING_FIELD_LABELS = {
  billingAddress: "missingField.billingAddress",
  format: "missingField.format",
  fullLegalName: "missingField.fullLegalName",
  iban: "missingField.iban",
  vatOrTaxNumber: "missingField.vatOrTaxNumber",
  deliveryOfService: "missingField.deliveryOfService",
} as const;

const FORMAT_LABELS: Record<EInvoiceFormat, string> = {
  XRECHNUNG_CII: "XRechnung (CII)",
  XRECHNUNG_UBL: "XRechnung (UBL)",
  ZUGFERD_EN16931: "ZUGFeRD EN 16931",
  ZUGFERD_EXTENDED: "ZUGFeRD Extended",
  ZUGFERD_XRECHNUNG: "ZUGFeRD XRechnung",
};

interface Props {
  invoice: InvoiceSubHeader_InvoiceFragment;
  project: InvoiceSubHeader_ProjectFragment;
}

export const InvoiceSubHeader = ({
  invoice,
  project,
  isHeaderVisible,
  setHeaderVisible,
}: Props & PageSubHeaderInjectedProps) => {
  const { t } = useTranslate(["Invoices", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const { openPdf, openAnyFile } = useOpenFile();
  const navigate = useNavigate();
  const { currentUser } = useUserData();

  const client = useApolloClient();
  const [modifyInvoice] = useModifyInvoiceMutation({ client });
  const [regenerateDocuments] =
    useInvoiceSubHeader_RegenerateInvoiceDocumentsMutation({ client });

  const invoiceFormat = invoice.eInvoiceFormat ?? null;
  const formatLabel = invoiceFormat ? FORMAT_LABELS[invoiceFormat] : null;
  const isXmlFormat =
    invoiceFormat === "XRECHNUNG_CII" || invoiceFormat === "XRECHNUNG_UBL";
  // Derive the download extension reliably even when eInvoiceFormat is missing
  // (legacy rows) or inconsistent: prefer the actual file URL extension, then
  // the stored mimeType, then fall back to the format flag.
  const extFromUrl = invoice.eInvoiceFile?.url
    ?.split("?")[0]
    .match(/\.([a-z0-9]+)$/i)?.[1]
    ?.toLowerCase();
  const eInvoiceExt =
    extFromUrl ??
    (invoice.eInvoiceFile?.mimeType === "application/pdf"
      ? "pdf"
      : invoice.eInvoiceFile?.mimeType?.includes("xml")
        ? "xml"
        : invoiceFormat
          ? isXmlFormat
            ? "xml"
            : "pdf"
          : "xml");

  const handleTitleChange = async (title: string) => {
    if (!project || !invoice) return;
    await modifyInvoice({
      variables: {
        input: {
          projectId: project?.id,
          docId: invoice?.quote?.id,
          invoiceId: invoice?.id,
          values: { title },
        },
      },
    });
  };

  const handleRegenerate = async () => {
    try {
      await regenerateDocuments({
        variables: {
          input: {
            projectId: project.id,
            docId: invoice.quote.id,
            invoiceId: invoice.id,
          },
        },
        context: { noErrorNotification: true },
      });
      await client.refetchQueries({ include: "active" });
    } catch (error) {
      if (error instanceof ApolloError) {
        const code = error.graphQLErrors[0]?.extensions?.["code"];
        if (code === "E_INVOICE_PRECONDITIONS_NOT_MET") {
          const fields = error.graphQLErrors[0]?.extensions?.[
            "missingFields"
          ] as string[] | undefined;
          // The service period is a per-invoice field; the rest are
          // organisation settings. Present them separately so a mixed list
          // never directs the user to the org settings for the service period.
          const { servicePeriodMissing, orgFields } =
            splitMissingFields(fields);
          const messages: string[] = [];
          if (orgFields.length > 0) {
            const translated = orgFields
              .map(f => {
                const label =
                  MISSING_FIELD_LABELS[f as keyof typeof MISSING_FIELD_LABELS];
                return label ? t(label, { ns: "Invoices" }) : f;
              })
              .join(", ");
            messages.push(
              `${t("E-invoice could not be generated. Please configure in the organisation settings:", { ns: "Invoices" })} ${translated}`
            );
          }
          if (servicePeriodMissing) {
            messages.push(t(SERVICE_PERIOD_MISSING_KEY, { ns: "Invoices" }));
          }
          enqueueSnackbar(
            messages.join(" ") ||
              t(
                "E-invoice could not be generated. Please configure in the organisation settings:",
                { ns: "Invoices" }
              ),
            { variant: "error" }
          );
        } else {
          enqueueSnackbar(t("An error occurred.", { ns: "Invoices" }), {
            variant: "error",
          });
        }
      } else {
        enqueueSnackbar(t("An error occurred.", { ns: "Invoices" }), {
          variant: "error",
        });
      }
    }
  };

  const hasEInvoice = !!invoice.eInvoiceFile?.url;
  const eInvoiceEnabled =
    currentUser?.organisation.eInvoiceSettings.enabled ?? false;

  return (
    <PageSubHeader
      title={invoice.title}
      hideTitleOnMobile
      handleTitleChange={
        invoice.status === "draft"
          ? async newValue => {
              await handleTitleChange(newValue);
              enqueueSnackbar(t("Title updated", { ns: "Global" }));
            }
          : undefined
      }
      titleEditTooltip={
        invoice.status === "draft" ? t("Rename", { ns: "Global" }) : undefined
      }
      status={<InvoiceStatusChip status={invoice.status} />}
      actionButtons={
        invoice.status === "draft" ? (
          <MenuButton>
            <RestrictedByProjectPermissionWithDebug
              permission="invoice_settings_update"
              project={project}
            >
              <ModalOpenButton
                Modal={InvoiceSettingsModal}
                modalProps={{
                  projectId: project.id,
                  invoiceId: invoice.id,
                  docId: invoice.quote.id,
                }}
              >
                <MenuItemWithIcon icon={<SettingsIcon />}>
                  {t("Invoice settings", { ns: "Invoices" })}
                </MenuItemWithIcon>
              </ModalOpenButton>
            </RestrictedByProjectPermissionWithDebug>
            <RestrictedByProjectPermissionWithDebug
              permission="invoice_document_update"
              project={project}
            >
              <InvoiceDraftDeleteMenuItem
                projectId={project.id}
                docId={invoice.quote.id}
                invoiceId={invoice.id}
                handleComplete={() => {
                  navigate(`/projects/${project.id}/invoices/outgoing`);
                }}
              />
            </RestrictedByProjectPermissionWithDebug>
          </MenuButton>
        ) : (
          <MenuButton
            Icon={<DownloadIcon />}
            tooltip={t("Download", { ns: "Global" })}
          >
            {(hasEInvoice || eInvoiceEnabled) && (
              <MenuItem
                disabled={!hasEInvoice}
                onClick={async () => {
                  if (invoice.eInvoiceFile?.url) {
                    await openAnyFile(
                      invoice.eInvoiceFile.url,
                      buildInvoiceFileName({
                        number: invoice.number,
                        id: invoice.id,
                        kind: "e-invoice",
                        ext: eInvoiceExt === "pdf" ? "pdf" : "xml",
                      }),
                      { filenameFromResponse: true }
                    );
                  }
                }}
              >
                <ListItemIcon style={{ minWidth: 36 }}>
                  <DownloadIcon />
                </ListItemIcon>
                <ListItemText
                  primary={t("Download e-invoice", { ns: "Invoices" })}
                  // Only surface the format subtitle when a file actually
                  // exists; on the disabled (no-file) state it would imply a
                  // downloadable format is available in inconsistent states.
                  secondary={hasEInvoice ? formatLabel : undefined}
                />
              </MenuItem>
            )}
            <MenuItem
              disabled={!invoice.file?.url}
              onClick={async () => {
                if (invoice.file?.url)
                  await openPdf(
                    invoice.file.url,
                    buildInvoiceFileName({
                      number: invoice.number,
                      id: invoice.id,
                      kind: "pdf",
                    }),
                    { filenameFromResponse: true }
                  );
              }}
            >
              <ListItemIcon style={{ minWidth: 36 }}>
                <DownloadIcon />
              </ListItemIcon>
              <ListItemText
                primary={t("Download plain PDF", { ns: "Invoices" })}
              />
            </MenuItem>
            <Divider />
            <RestrictedByProjectPermissionWithDebug
              permission="invoice_document_update"
              project={project}
            >
              <MenuItemWithIcon
                icon={<RefreshIcon />}
                onClick={handleRegenerate}
              >
                {t("Regenerate Invoice", { ns: "Invoices" })}
              </MenuItemWithIcon>
            </RestrictedByProjectPermissionWithDebug>
          </MenuButton>
        )
      }
      entityNumber={
        invoice.status !== "draft" && (
          <Typography variant="body2">
            <EntityNumber number={invoice.number ?? undefined} />
          </Typography>
        )
      }
      isHeaderVisible={isHeaderVisible}
      setHeaderVisible={setHeaderVisible}
    />
  );
};
